import { ref, watch, onUnmounted } from 'vue';

// Tracks whether a scrollable element has more content hidden past its left
// and/or right edges, so a template can show a fade/shadow cue there — a
// wide table with `overflow-x: auto` otherwise gives no hint there's
// anything past the initial view, especially on a trackpad or mobile where
// the native scrollbar is thin or invisible. `elRef` is a template ref to
// the actual scrolling element (the one with `overflow-x`/`overflow` set).
//
// Watches `elRef` itself, rather than attaching once in onMounted, because
// the scrolling element isn't always there yet when the owning component
// mounts — e.g. a table behind `v-if="archer"` that only exists once an
// async scan completes. Attaching in onMounted would silently bind to
// nothing (elRef.value still null) and never retry, leaving the shadows
// frozen at whatever the caller's own one-off `update()` call happened to
// compute right after the data arrived, never reacting to real scrolling
// after that (found exactly this way: the score-sheet-scanner table's
// shadows never moved after the initial post-scan render). Watching
// re-attaches whenever the actual DOM element appears, changes, or
// disappears — including the ordinary case where it's there from the start.
export function useScrollEdges(elRef) {
  const canScrollLeft = ref(false);
  const canScrollRight = ref(false);
  const resizeObserver = new ResizeObserver(update);
  let observedEl = null;

  function update() {
    const el = elRef.value;
    if (!el) {
      canScrollLeft.value = false;
      canScrollRight.value = false;
      return;
    }
    canScrollLeft.value = el.scrollLeft > 1;
    canScrollRight.value = Math.ceil(el.scrollLeft) < el.scrollWidth - el.clientWidth - 1;
  }

  watch(
    elRef,
    (el) => {
      if (observedEl) {
        observedEl.removeEventListener('scroll', update);
        resizeObserver.unobserve(observedEl);
      }
      observedEl = el;
      if (el) {
        el.addEventListener('scroll', update, { passive: true });
        resizeObserver.observe(el);
      }
      update();
    },
    { immediate: true }
  );

  window.addEventListener('resize', update);

  onUnmounted(() => {
    if (observedEl) observedEl.removeEventListener('scroll', update);
    resizeObserver.disconnect();
    window.removeEventListener('resize', update);
  });

  return { canScrollLeft, canScrollRight, update };
}
